import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
} from "@mui/material";
// import Cookies from "js-cookie";
import axios from "axios";

const riskQuestions = [
  { level: "Level 1", items: [{ text: "Suicidal ideation? (Since last session if this is a follow-up.)", name: "suicidalIdeation", hasTextField: true }] },
  {
    level: "Level 2",
    items: [
      { text: "Suicide Risk (Ideation)", name: "suicideRisk", hasTextField: true },
      { text: "Plan/Method (Has a suicide plan/method)", name: "planMethod", hasTextField: true },
      { text: "Intent (Suicide intent)", name: "intent", hasTextField: true },
      { text: "Impulse control", name: "impulseControl", hasTextField: true },
      { text: "Agitation", name: "agitation", hasTextField: true },
      { text: "Perceived burdensomeness", name: "perceivedBurdensomeness", hasTextField: true },
      { text: "Sleep difficulties (insomnia, nightmares)", name: "sleepDifficulties", hasTextField: true },
      { text: "Mania/Hypomania", name: "maniaHypomania", hasTextField: true },
      { text: "Psychosis", name: "psychosis", hasTextField: true },
      { text: "Substance abuse", name: "substanceAbuse", hasTextField: true },
      { text: "Lifetime history of suicide attempts", name: "suicideAttemptsHistory", hasTextField: true },
    ],
  },
  {
    level: "Level 3",
    items: [
      { text: "Family history of suicide/suicidal behavior", name: "familyHistorySuicide", hasTextField: true },
      { text: "Recent suicide of friend/family member", name: "recentSuicide", hasTextField: true },
      { text: "Recent death of a family member/friend", name: "recentDeath", hasTextField: true },
      { text: "Recent breakup or loss of relationship", name: "recentBreakup", hasTextField: true },
      { text: "Physical illness", name: "physicalIllness", hasTextField: true },
      { text: "Hopelessness", name: "hopelessness", hasTextField: true },
      { text: "Helplessness", name: "helplessness", hasTextField: true },
      { text: "Demographic vulnerability (male, GLBT, int'l student)", name: "demographicVulnerability", hasTextField: true },
      { text: "Legal/Arrest history", name: "legalHistory", hasTextField: true },
      { text: "Anti-social traits", name: "antisocialTraits", hasTextField: true },
    ],
  },
  {
    level: "Proactive Factors",
    items: [
      { text: "Cooperative alliances with a mental health professional", name: "cooperativeAlliances", hasTextField: true },
      { text: "Physiological resilience and/or good problem solving skills", name: "physiologicalResilience", hasTextField: true },
      { text: "Available/accessible social support", name: "socialSupport", hasTextField: true },
      { text: "Intact relationship/young children", name: "intactRelationship", hasTextField: true },
      { text: "Hopefulness/plans for the future", name: "hopefulness", hasTextField: true },
      { text: "Fear of hurting/disappointing others", name: "fearHurtingOthers", hasTextField: true },
      { text: "Religious/moral beliefs", name: "religiousBeliefs", hasTextField: true },
      { text: "Motivated for treatment", name: "motivatedTreatment", hasTextField: true },
      { text: "Fear of legal consequences", name: "fearLegalConsequences", hasTextField: true },
    ],
  },
  {
    level: "Interventions",
    items: [
      { text: "Identified reason for living", name: "reasonForLiving", hasTextField: true },
      { text: "Consultation with psychiatrist", name: "consultPsychiatrist", hasTextField: true },
      { text: "Consultation with clinic director", name: "consultClinicDirector", hasTextField: true },
      { text: "Consultation with crisis counselor", name: "consultCrisisCounselor", hasTextField: true },
      { text: "Consultation (indicate with whom, when and the nature of contact)", name: "consultationOther", hasTextField: true },
      { text: "Worked to reduce perceived burdensomeness", name: "reduceBurdensomeness", hasTextField: true },
      { text: "Worked to decrease shame", name: "decreaseShame", hasTextField: true },
    ],
  },
  {
    level: "Post-Intervention",
    items: [
      { text: "After intervention is hospitalization recommended?", name: "hospitalizationRecommended", hasTextField: true },
      { text: "Did risk level decrease during session?", name: "riskLevelDecreased", hasTextField: true },
      { text: "Homicidal ideation?", name: "homicidalIdeation", hasTextField: true },
      { text: "Assessment of current suicide risk", name: "currentSuicideRisk", hasTextField: true },
      { text: "Assessment of current homicide risk", name: "currentHomicideRisk", hasTextField: true },
    ],
  },
  {
    level: "Substance Use Questions",
    items: [
      { text: "Have you ever felt that you ought to cut down on your drinking or drug use?", name: "question1" },
      { text: "Have people annoyed you by criticizing your drinking or drug use?", name: "question2" },
      { text: "Have you ever felt bad or guilty about your drinking or drug use?", name: "question3" },
      { text: "Have you ever had a drink or used drugs first thing in the morning to steady your nerves or to get rid of a hangover?", name: "question4" },
    ],
  },
];

const BASE = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL || "https://neuroez4.devforhealth.com/api";

const valueOrDash = (v) => {
  if (Array.isArray(v)) {
    const list = v.map((x) => (typeof x === "string" ? x.trim() : x)).filter(Boolean);
    return list.length ? list.join(", ") : "—";
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "—";
  const s = String(v);
  return s.trim() === "" ? "—" : s;
};
const splitList = (s) =>
  typeof s === "string"
    ? s.split(";").map((x) => x.trim()).filter(Boolean)
    : Array.isArray(s)
      ? s.filter(Boolean)
      : [];
const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};
const parseMaybeDoubleEncoded = (input) => {
  try {
    let out = input;
    if (typeof out === "string") out = JSON.parse(out);
    if (typeof out === "string") out = JSON.parse(out);
    return out;
  } catch {
    return input;
  }
};

const Field = ({ label, value }) => (
  <Box mb={1}>
    <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
    <Typography variant="body2" color="text.secondary">{valueOrDash(value)}</Typography>
  </Box>
);
const Symptom = ({ label, details, checked }) => {
  const shown = details ?? (checked ? "Yes" : "—");
  return <Field label={label} value={shown} />;
};

const StickyTitleHeader = ({ title, DOS }) => {
  const fmtDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };
  return (
    <Box sx={{ position: "sticky", top: "-16px", zIndex: (t) => t.zIndex.appBar, bgcolor: "#fff", p: 4, pb: 0, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
        <strong>DOS:</strong> {fmtDate(DOS)}
      </Typography>
      <Typography variant="h6" sx={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", fontWeight: "bold" }}>
        {title || "Adult Diagnostic Assessment"}
      </Typography>
      <Box sx={{ height: 28 }} />
      <Divider />
    </Box>
  );
};

const phq9Items = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
  "Trouble concentrating on things, such as reading the newspaper or watching television",
  "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
  "Thoughts that you would be better off dead or of hurting yourself in some way",
];
const gad7Items = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid as if something awful might happen",
];
const scoreToFreq = (n) => {
  const v = Number(n);
  if (v === 0) return "Not at all";
  if (v === 1) return "Several days";
  if (v === 2) return "More than half the days";
  if (v === 3) return "Nearly every day";
  return "";
};
const phqSeverity = (t) => {
  const v = Number(t);
  if (v <= 4) return "Minimal";
  if (v <= 9) return "Mild";
  if (v <= 14) return "Moderate";
  if (v <= 19) return "Moderately severe";
  return "Severe";
};
const gadSeverity = (t) => {
  const v = Number(t);
  if (v <= 4) return "Minimal";
  if (v <= 9) return "Mild";
  if (v <= 14) return "Moderate";
  return "Severe";
};
const normalizeSurveyObj = (raw) => {
  if (!raw) return {};
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      obj = {};
    }
  }
  return obj || {};
};
const difficultyFromFlags = (o = {}) => {
  if (o.extremelyDifficult) return "Extremely difficult";
  if (o.vertDifficult || o.veryDifficult) return "Very difficult";
  if (o.someWhatDifiicult || o.somewhatDifficult) return "Somewhat difficult";
  if (o.difficultAll || o.notDifficultAtAll) return "Not difficult at all";
  return "";
};

const AdultDiagnosticAssessmentPreview = (props) => {
  const { data, formData } = props || {};
  const patientFormId = props?.patientFormId ?? data?.patientFormId;
  const formTitle = props?.formTitle ?? data?.formTitle;

  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (formData || data) {
      const src = formData ?? data;
      const ad = src?.adultDiagnostic ?? src;
      setNote(ad || null);
      setMeta({
        practiceName: pick(src, ["practiceName", "particeName"]) ?? ad?.practiceName ?? "",
        accountNo: pick(src, ["accountNo", "fileno", "fileNo"]) ?? ad?.accountNo ?? "",
        assessmentDate: pick(src, ["assessmentDate", "asssessmentDate"]) ?? ad?.assessmentDate ?? ad?.asssessmentDate ?? "",
        patientName: pick(src, ["patientName", "PatientNameclient", "clientName"]) ?? ad?.patientName ?? ad?.clientName ?? "",
        dob: pick(src, ["dob", "dOB", "DOB"]) ?? ad?.dob ?? ad?.dOB ?? "",
        customFormTitle: src.customFormTitle ?? ad?.customFormTitle ?? "",
        providerDate: src.providerDate ?? ad?.providerDate ?? "",
        supervisorSignatureDATE: src.supervisorSignatureDATE ?? ad?.supervisorSignatureDATE ?? "",
        staffsignatureDATE: src.staffsignatureDATE ?? ad?.staffsignatureDATE ?? "",
        supervisedDate: src.supervisedDate ?? ad?.supervisedDate ?? "",
        supervisedClinicianMsg:
          pick(src, ["messagesupervisedClinician", "messageSupervisedClinician", "supervisedClinicianMessage"]) ??
          pick(ad || {}, ["messagesupervisedClinician", "messageSupervisedClinician", "supervisedClinicianMessage"]),
        internMsg:
          pick(src, ["messageinternSign", "messageInternSign", "internMessage"]) ??
          pick(ad || {}, ["messageinternSign", "messageInternSign", "internMessage"]),
        providerMsg:
          pick(src, ["message", "messageProviderSignature", "messageProfessionalSignature", "messageProviderSign", "massagestaffSignature"]) ??
          pick(ad || {}, ["message", "messageProviderSignature", "messageProfessionalSignature", "messageProviderSign", "massagestaffSignature"]),
        supervisorMsg:
          pick(src, ["messageSupervisorSignature", "messageSupervisorSign", "supervisorMessage"]) ??
          pick(ad || {}, ["messageSupervisorSignature", "messageSupervisorSign", "supervisorMessage"]),
      });
    }
  }, [formData, data]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!patientFormId) return;
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')

        const client = axios.create({
          baseURL: BASE,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/json",
          },
        });

        const [pfRes, adRes] = await Promise.all([
          client.get(`/Forms/GetPatientFormsbyId?Id=${patientFormId}`),
          client.get(`/AdultDiagnosticAssessment/AdultDiagnosticAssesment?id=${patientFormId}`),
        ]);

        if (!mounted) return;

        const pfParsed = parseMaybeDoubleEncoded(pfRes.data);
        const pf = Array.isArray(pfParsed) ? pfParsed[0] || {} : pfParsed || {};

        const adParsed = parseMaybeDoubleEncoded(adRes.data);
        const adultDiagnostic = adParsed?.adultDiagnostic || adParsed || null;

        setMeta({
          practiceName: pf.practiceName,
          accountNo: pf.accountNo,
          assessmentDate: pf.assessmentDate,
          patientName: pf.patientName,
          dob: pf.dob,
          customFormTitle: pf.customFormTitle,
          providerDate: pf.providerDate,
          supervisorSignatureDATE: pf.supervisorSignatureDATE,
          staffsignatureDATE: pf.staffsignatureDATE,
          supervisedDate: pf.supervisedDate,
          supervisedClinicianMsg:
            pick(pf, ["messagesupervisedClinician", "messageSupervisedClinician", "supervisedClinicianMessage"]) ??
            pick(adultDiagnostic || {}, ["messagesupervisedClinician", "messageSupervisedClinician", "supervisedClinicianMessage"]),
          internMsg:
            pick(pf, ["messageinternSign", "messageInternSign", "internMessage"]) ??
            pick(adultDiagnostic || {}, ["messageinternSign", "messageInternSign", "internMessage"]),
          providerMsg:
            pick(pf, ["message", "messageProviderSignature", "messageProfessionalSignature", "messageProviderSign", "massagestaffSignature"]) ??
            pick(adultDiagnostic || {}, ["message", "messageProviderSignature", "messageProfessionalSignature", "messageProviderSign", "massagestaffSignature"]),
          supervisorMsg:
            pick(pf, ["messageSupervisorSignature", "messageSupervisorSign", "supervisorMessage"]) ??
            pick(adultDiagnostic || {}, ["messageSupervisorSignature", "messageSupervisorSign", "supervisorMessage"]),
        });
        setNote(adultDiagnostic);
      } catch (e) {
        if (mounted) setError("Failed to load preview.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [patientFormId]);

  const DOS = useMemo(() => pick(meta ?? note ?? {}, ["assessmentDate", "asssessmentDate"]) || "", [meta, note]);
  const patient = useMemo(() => pick(meta ?? note ?? {}, ["patientName", "PatientNameclient", "clientName"]) || "", [meta, note]);
  const dob = useMemo(() => pick(meta ?? note ?? {}, ["dob", "dOB", "DOB"]) || "", [meta, note]);
  const accountNo = useMemo(() => pick(meta ?? note ?? {}, ["accountNo", "fileno", "fileNo"]) || "", [meta, note]);

  const phqObj = useMemo(() => normalizeSurveyObj(note?.patientPHQ9), [note]);
  const gadObj = useMemo(() => normalizeSurveyObj(note?.patientGAD7), [note]);

  const phqScores = useMemo(() => {
    const arr = Array.from({ length: 9 }, (_, i) => Number(phqObj[`q${i + 1}`] ?? 0));
    const sum = phqObj.totalScore != null ? Number(phqObj.totalScore) : arr.reduce((a, b) => a + b, 0);
    return { arr, total: sum, diff: difficultyFromFlags(phqObj), severity: phqSeverity(sum) };
  }, [phqObj]);

  const gadScores = useMemo(() => {
    const arr = Array.from({ length: 7 }, (_, i) => Number(gadObj[`q${i + 1}`] ?? 0));
    const sum = gadObj.totalScore != null ? Number(gadObj.totalScore) : arr.reduce((a, b) => a + b, 0);
    return { arr, total: sum, diff: difficultyFromFlags(gadObj), severity: gadSeverity(sum) };
  }, [gadObj]);

  const fmtDate = (val) => {
    const v = valueOrDash(val);
    if (v === "—") return v;
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString();
  };
  const fmtTime = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  };
  const sigDisplay = (msg, dateVal) => {
    const m = String(msg || "").trim();
    if (m) return m;
    const d = fmtDate(dateVal);
    return d !== "—" ? `Signed on ${d}` : "Pending signature";
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !note) {
    return (
      <Typography variant="body2" color="text.secondary" align="center">
        {error || "No data available for preview."}
      </Typography>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 0, bgcolor: "#fff", borderRadius: 2, boxShadow: "0 3px 8px rgba(0,0,0,0.08)" }}>
      <StickyTitleHeader title={formTitle || meta?.customFormTitle || note.customFormTitle || "Adult Diagnostic Assessment"} DOS={DOS} />

      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {valueOrDash(accountNo)}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client:</strong> {valueOrDash(patient)}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {fmtDate(dob)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <Typography variant="h6" gutterBottom color="primary">Diagnostic Assessment</Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth={false}
              size="small"
              label="Title"
              value={meta?.customFormTitle || note.customFormTitle || "—"}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth={false}
              size="small"
              label="Assessment date"
              value={fmtDate(DOS)}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth={false}
              size="small"
              label="Start Time:"
              value={fmtTime(note.startTime)}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "228px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth={false}
              size="small"
              label="End Time:"
              value={fmtTime(note.endTime)}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "228px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth={false}
              size="small"
              label="Date of Assessment Interviews:"
              value={fmtDate(note.assessmentInterviewDate)}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "228px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth={false}
              size="small"
              label="Contributions to the Assessment"
              value={splitList(note.contributional).join("\n") || "—"}
              InputProps={{ readOnly: true, disabled: true }}
              multiline
              minRows={2}
              sx={{
                "& .MuiInputBase-root": {
                  height: 50,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "715px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Current Life Situation</Typography>
        <Field label="Age" value={note.age} />
        <Field label="Living situation" value={note.livingSituation} />
        <Field label="Basic needs and economic status" value={note.basicNeed} />
        <Field label="Education" value={note.education} />
        <Field label="Significant Personal Relationships" value={note.significiant} />
        <Field label="Strengths and resources" value={note.strengths} />
        <Field label="Belief systems" value={note.beleifs} />
        <Field label="Contextual non-personal factors" value={note.contextual} />
        <Field label="General physical health and relationship to client's culture" value={note.phisicalHealth} />
        <Field label="Current medications" value={note.medication} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Presenting Problems & Reason for Referral</Typography>
        <Field label="Perception of condition" value={note.preceptionCondition} />
        <Field label="Description of symptoms" value={note.descriptionSymptoms} />
        <Field label="Reason for referral" value={note.referrel} />
        <Field label="History of mental health and treatment" value={note.history} />
        <Field label="Important Developmental Incidents" value={note.importantIncident} />
        <Field label="Trauma history" value={note.tramaHistory} />
        <Field label="Maltreatment or abuse issues" value={note.malTreatment} />
        <Field label="History of alcohol and drug usage and treatment" value={note.drugHistory} />
        <Field label="Health history" value={note.healthHistory} />
        <Field label="Family health history" value={note.familyHealthHistory} />
        <Field label="Cultural influences and their impact on the client" value={note.cultural} />
        <Field label="Communication style" value={note.communication} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Mental Status Exam</Typography>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>General Observations</Typography>
        <Field label="Appearance" value={note.appearance} />
        <Field label="Build" value={note.build} />
        <Field label="Demeanor/Response to Interviewer" value={note.demeanor} />
        <Field label="Eye Contact" value={note.eyeContact} />
        <Field label="Activity Level" value={note.activityLevel} />
        <Field label="Speech" value={note.speech} />
        <Typography sx={{ fontWeight: 700, my: 1 }}>Thought Content and Perception</Typography>
        <Field label="Delusions" value={pick(note, ["delusions", "delusionsPTSD"])} />
        <Field label="Hallucinations" value={pick(note, ["hallucinations", "hallucinationsPTSD"])} />
        <Typography sx={{ fontWeight: 700, my: 1 }}>Self-Danger</Typography>
        <Field label="Self-Abuse" value={note.selfAbuse} />
        <Field label="Violence to Others" value={note.violence} />
        <Field label="Destruction to Property" value={pick(note, ["destruction", "destructionToProperty"])} />
        <Typography sx={{ fontWeight: 700, my: 1 }}>Cognition</Typography>
        <Field label="Orientation" value={note.orientation} />
        <Field label="Memory Issues" value={note.memoryIssues} />
        <Field label="Attention / Concentration" value={note.attention} />
        <Field label="Judgment/Insight" value={note.judgment} />
        <Typography sx={{ fontWeight: 700, my: 1 }}>Thought and Perceptual Process</Typography>
        <Field label="Mood" value={note.mood} />
        <Field label="Affect" value={note.affect} />
        <Field label="Behavior" value={note.behavior} />
        <Field label="Anxiety" value={note.anxiety} />
        <Field label="Intelligence Estimate" value={note.intelligenceEstimate} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Depression Disorders</Typography>
        <Symptom label="Depressed mood most of the day" details={note.depressedMood} checked={note.depressedMoodCheckbox} />
        <Symptom label="Diminished interest in activities" details={note.diminishedInterest} checked={note.diminishedInterestCheckbox} />
        <Symptom label="Insomnia/hypersomnia" details={note.insomnia} checked={note.insomniaCheckbox} />
        <Symptom label="Appetite too much/too little" details={note.appetite} checked={note.appetiteCheckbox} />
        <Symptom label="Significant weight loss/gain" details={note.weightChange} checked={note.weightChangeCheckbox} />
        <Symptom label="Feelings of worthlessness/guilt" details={note.worthlessness} checked={note.worthlessnessCheckbox} />
        <Symptom label="Psychomotor agitation/retardation" details={note.psychomotor} checked={note.psychomotorCheckbox} />
        <Symptom label="Fatigue/energy loss" details={note.fatigue} checked={note.fatigueCheckbox} />
        <Symptom label="Diminished ability to concentrate/make decisions" details={note.concentration} checked={note.concentrationCheckbox} />
        <Symptom label="Recurrent thoughts of death/suicide" details={note.suicideThoughts} checked={note.suicideThoughtsCheckbox} />
        <Symptom label="History of self-harm/suicide attempt" details={note.selfHarm} checked={note.selfHarmCheckbox} />
        <Symptom label="Other" details={note.otherDepression} checked={note.otherDepressionCheckbox} />
        <Symptom label="Low Self Esteem (Dysthymic Disorder Only)" details={note.lowSelfEsteem} checked={note.lowSelfEsteemCheckbox} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Generalized Anxiety Disorder</Typography>
        <Symptom label="Excessive anxiety/worry" details={note.excessiveAnxiety} checked={note.excessiveAnxietyCheckbox} />
        <Symptom label="Difficult to control worry" details={note.controlWorry} checked={note.controlWorryCheckbox} />
        <Symptom label="Easily fatigued" details={note.easilyFatigued} checked={note.easilyFatiguedCheckbox} />
        <Symptom label="Difficulty concentrating/Mind going blank" details={note.difficultyConcentrating} checked={note.difficultyConcentratingCheckbox} />
        <Symptom label="Irritability" details={note.irritability} checked={note.irritabilityCheckbox} />
        <Symptom label="Restlessness/On edge" details={note.restlessness} checked={note.restlessnessCheckbox} />
        <Symptom label="Muscle tension" details={note.muscleTension} checked={note.muscleTensionCheckbox} />
        <Symptom label="Disturbed sleep" details={note.disturbedSleep} checked={note.disturbedSleepCheckbox} />
        <Symptom label="Other" details={note.otherAnxiety} checked={note.otherAnxietyCheckbox} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Additional Symptomology & Mania</Typography>
        <Symptom label="Panic Attacks" details={note.panicAttacks} checked={note.panicAttacksCheckbox} />
        <Symptom label="Agoraphobia" details={note.agoraphobia} checked={note.agoraphobiaCheckbox} />
        <Symptom label="Specific Phobia" details={note.specificPhobia} checked={note.specificPhobiaCheckbox} />
        <Symptom label="Obsessive Compulsive Disorder" details={note.ocd} checked={note.ocdCheckbox} />
        <Symptom label="Inflated self-esteem/grandiosity" details={note.selfEsteem} checked={note.selfEsteemCheckbox} />
        <Symptom label="Decreased need for sleep" details={note.sleepNeed} checked={note.sleepNeedCheckbox} />
        <Symptom label="Pressured speech/increased talking" details={note.speechAdditionalSymptomology} checked={note.speechAdditionalSymptomologyCheckbox} />
        <Symptom label="Flight of ideas/racing thoughts" details={note.racingThoughts} checked={note.racingThoughtsCheckbox} />
        <Symptom label="Distractible" details={note.distractible} checked={note.distractibleCheckbox} />
        <Symptom label="Increase in goal directed activity" details={note.goalActivity} checked={note.goalActivityCheckbox} />
        <Symptom label="Psychomotor agitation" details={note.psychomotorAgitation} checked={note.psychomotorAgitationCheckbox} />
        <Symptom label="Excessive risky behaviors" details={note.riskyBehaviors} checked={note.riskyBehaviorsCheckbox} />
        <Symptom label="Hypomania" details={note.hypomania} checked={note.hypomaniaCheckbox} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">PTSD & Schizophrenia</Typography>
        <Symptom label="Experienced/witnessed life threatening event" details={note.lifeThreateningEvent} checked={note.lifeThreateningEventCheckbox} />
        <Symptom label="Responded with fear/helplessness" details={note.fearHelplessness} checked={note.fearHelplessnessCheckbox} />
        <Symptom label="Recurrent distressing recollections of event" details={note.distressingRecollections} checked={note.distressingRecollectionsCheckbox} />
        <Symptom label="Recurrent dreams of event" details={note.recurrentDreams} checked={note.recurrentDreamsCheckbox} />
        <Symptom label="Feeling or acting as if event was reoccurring" details={note.eventReoccurring} checked={note.eventReoccurringCheckbox} />
        <Symptom label="Intense distress when exposed to aspect of trauma" details={note.intenseDistress} checked={note.intenseDistressCheckbox} />
        <Symptom label="Persistent avoidance of stimuli associated with event" details={note.avoidanceStimuli} checked={note.avoidanceStimuliCheckbox} />
        <Symptom label="Difficulty sleeping" details={note.difficultySleeping} checked={note.difficultySleepingCheckbox} />
        <Symptom label="Irritability/anger outbursts" details={note.irritabilityPTSD} checked={note.irritabilityCheckboxPTSD} />
        <Symptom label="Difficulty concentrating" details={note.difficultyConcentratingPTSD} checked={note.difficultyConcentratingCheckboxPTSD} />
        <Symptom label="Hyper vigilance/exaggerated startle response" details={note.hyperVigilance} checked={note.hyperVigilanceCheckbox} />
        <Symptom label="Delusions" details={note.delusionsPTSD} checked={note.delusionsCheckbox} />
        <Symptom label="Hallucinations" details={note.hallucinationsPTSD} checked={note.hallucinationsCheckboxPTSD} />
        <Symptom label="Disorganized speech" details={note.disorganizedSpeech} checked={note.disorganizedSpeechCheckbox} />
        <Symptom label="Grossly disorganization / catatonic" details={note.grossDisorganization} checked={note.grossDisorganizationCheckbox} />
        <Symptom label="Flat affect" details={note.flatAffect} checked={note.flatAffectCheckbox} />
        <Symptom label="Lack of speech" details={note.lackOfSpeech} checked={note.lackOfSpeechCheckbox} />
        <Symptom label="Lack of motivation" details={note.lackOfMotivation} checked={note.lackOfMotivationCheckbox} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Screenings</Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>PHQ-9 Depression Screening</Typography>
        {note?.patientPHQ9 ? (
          <TableContainer sx={{ mb: 1, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>Score</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Frequency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phq9Items.map((text, idx) => {
                  const score = phqScores.arr[idx] ?? 0;
                  return (
                    <TableRow key={`phq-${idx}`}>
                      <TableCell>{idx + 1}. {text}</TableCell>
                      <TableCell>{score}</TableCell>
                      <TableCell>{scoreToFreq(score)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Field label="PHQ-9" value="—" />
        )}
        {note?.patientPHQ9 && (
          <>
            <Field label="PHQ-9 Total" value={`${phqScores.total} (${phqSeverity(phqScores.total)})`} />
            <Field label="Difficulty" value={phqScores.diff || "—"} />
          </>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>GAD-7 Anxiety Screening</Typography>
        {note?.patientGAD7 ? (
          <TableContainer sx={{ mb: 1, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>Score</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Frequency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gad7Items.map((text, idx) => {
                  const score = gadScores.arr[idx] ?? 0;
                  return (
                    <TableRow key={`gad-${idx}`}>
                      <TableCell>{idx + 1}. {text}</TableCell>
                      <TableCell>{score}</TableCell>
                      <TableCell>{scoreToFreq(score)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Field label="GAD-7" value="—" />
        )}
        {note?.patientGAD7 && (
          <>
            <Field label="GAD-7 Total" value={`${gadScores.total} (${gadSeverity(gadScores.total)})`} />
            <Field label="Difficulty" value={gadScores.diff || "—"} />
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Optional Assessments (GAIN-SS)</Typography>
        <Field label="IDScr Past Month" value={note.idScrPastMonth} />
        <Field label="IDScr Past 90 Days" value={note.idScrPast90Days} />
        <Field label="IDScr Past Year" value={note.idScrPastYear} />
        <Field label="IDScr Ever" value={note.idScrEver} />
        <Field label="EDScr Past Month" value={note.edScrPastMonth} />
        <Field label="EDScr Past 90 Days" value={note.edScrPast90Days} />
        <Field label="EDScr Past Year" value={note.edScrPastYear} />
        <Field label="EDScr Ever" value={note.edScrEver} />
        <Field label="SDScr Past Month" value={note.sdScrPastMonth} />
        <Field label="SDScr Past 90 Days" value={note.sdScrPast90Days} />
        <Field label="SDScr Past Year" value={note.sdScrPastYear} />
        <Field label="SDScr Ever" value={note.sdScrEver} />
        <Field label="CVScr Past Month" value={note.cvScrPastMonth} />
        <Field label="CVScr Past 90 Days" value={note.cvScrPast90Days} />
        <Field label="CVScr Past Year" value={note.cvScrPastYear} />
        <Field label="CVScr Ever" value={note.cvScrEver} />
        <Field label="TDScr Past Month" value={note.tdScrPastMonth} />
        <Field label="TDScr Past 90 Days" value={note.tdScrPast90Days} />
        <Field label="TDScr Past Year" value={note.tdScrPastYear} />
        <Field label="TDScr Ever" value={note.tdScrEver} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">WHODAS</Typography>
        <Grid container spacing={2}>
          {Array.from({ length: 12 }, (_, i) => `s${i + 1}`).map((k) => (
            <Grid item xs={12} md={6} key={k}>
              <Field label={k.toUpperCase()} value={note[k]} />
            </Grid>
          ))}
          <Grid item xs={12} md={6}><Field label="Total Item Score (/48)" value={note.totalItemScore} /></Grid>
          <Grid item xs={12} md={6}><Field label="Total Percentage (%)" value={note.totalPercentage} /></Grid>
        </Grid>
        {note.brief ? <Field label="Screened positive = a score of 1 or greater" value="Yes" /> : null}
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Client Needs</Typography>
        <Field label="Assessment of client needs" value={splitList(note.clientNeeds)} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Clinical Summary</Typography>
        <Field label="Summary" value={note.clinicalSummary} />
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom color="primary">Recommendations for further services that are medically necessary</Typography>
        <Field label="Further Services" value={note.furtherServices} />
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom color="primary">Provisional diagnostic hypothesis</Typography>
        <Field label="Hypothesis" value={note.diagnosticHypothesis} />
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Diagnosis</Typography>
        {Array.isArray(note?.dsm5Diagnosis) && note.dsm5Diagnosis.length > 0 ? (
          note.dsm5Diagnosis.map((dx, idx) => (
            <Box key={idx} mb={1.5} sx={{ pl: 1, borderLeft: "3px solid #e0e0e0" }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>DSM-5 Diagnosis {idx + 1}</Typography>
              <Field label="Principal Diagnosis" value={dx?.principalDiagnosis} />
              <Field label="Provisional Diagnosis" value={dx?.provisionalDiagnosis} />
              <Field label="Diagnosis" value={dx?.diagnosis} />
              <Field label="Other Specified/Unspecified Disorders" value={dx?.otherDisorders} />
              <Field label="Medical" value={dx?.medical} />
              <Field label="DSM-5 Code" value={dx?.dsM5} />
              <Field label="DSM-5 Detail" value={dx?.dsM5Detail} />
              <Field label="ICD-10 Code" value={dx?.icD10} />
              <Field label="ICD-10 Detail" value={dx?.icD10Detail} />
              <Field label="Specifier" value={dx?.specifier} />
              <Field label="Specifier Explain" value={dx?.specifierExplain} />
              <Field label="Subtype" value={dx?.subtype} />
              <Field label="Subtype Explain" value={dx?.subtypeExplain} />
              <Field label="Severity" value={dx?.severity} />
              <Field label="Severity Explain" value={dx?.severityExplain} />
              <Field label="Other Factors" value={dx?.otherFactors} />
            </Box>
          ))
        ) : (
          <Field label="DSM-5 Diagnosis" value="—" />
        )}
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Supportive Assessment(s) / Functional Assessment(s) and Screening(s)</Typography>
        {Array.isArray(note.supportiveAssessment) && note.supportiveAssessment.length > 0 ? (
          note.supportiveAssessment.map((sa, idx) => (
            <Box key={idx} mb={1.5} sx={{ pl: 1, borderLeft: "3px solid #e0e0e0" }}>
              <Field label="Date" value={sa?.date ? new Date(sa.date).toLocaleDateString() : "—"} />
              <Field label="Name" value={sa?.name} />
              <Field label="Description" value={sa?.description} />
              <Field label="Notes" value={sa?.notes} />
            </Box>
          ))
        ) : (
          <Field label="Supportive Assessments" value="—" />
        )}
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom color="primary">Signatures</Typography>
        <Field label="Supervised Clinician:" value={sigDisplay(meta?.supervisedClinicianMsg, meta?.supervisedDate || note?.supervisedDate)} />
        <Field label="Intern Signature:" value={sigDisplay(meta?.internMsg, meta?.staffsignatureDATE || note?.staffsignatureDATE)} />
        <Field label="Provider Signature:" value={sigDisplay(meta?.providerMsg, meta?.providerDate || note?.providerDate)} />
        <Field label="Supervisor Signature:" value={sigDisplay(meta?.supervisorMsg, meta?.supervisorSignatureDATE || note?.supervisorSignatureDATE)} />
      </Box>
    </Paper>
  );
};

export default AdultDiagnosticAssessmentPreview;